import { z } from "zod";

export const branchSchema = z.object({
  name: z.string().min(3, "Branch name must be at least 3 characters"),
  email: z.email("Invalid email address"),
  phoneNumber: z
    .string()
    .regex(/^\+?(\d{1,4})?\d{10}$/, "Invalid phone number"),
  address: z.object({
    region: z.string().min(3, "Region must be at least 3 characters"),
    city: z.string().min(2, "City must be at least 2 characters"),
    kifleKetema: z
      .string()
      .min(3, "Kifle Ketema must be at least 3 characters"),
    woreda: z.string().min(1, "Woreda must be at least 1 character"),
  }),
  contactPerson: z.object({
    fullName: z.string().min(4, "Full name must be at least 4 characters"),
    username: z.string().min(3, "Username must be at least 3 characters"),
    email: z.email("Invalid contact email"),
    phoneNumber: z
      .string()
      .regex(/^\+?(\d{1,4})?\d{10}$/, "Invalid contact phone number"),
  }),
  branch: z.string().optional(),
  type: z.string().optional(),
});

export type BranchFormData = z.infer<typeof branchSchema>;

export const orderSchema = z
  .object({
    productType: z.string().min(1, "Product type is required"),
    product: z.number().min(1, "Product is required"),
    location: z.string().min(1, "Location is required"),
    quantity: z
      .string()
      .regex(/^\d+$/, "Quantity must be a number")
      .refine(
        (val) => parseInt(val, 10) > 0,
        "Quantity must be greater than 0"
      ),
    startDate: z.string().min(1, "Start date is required"),
    dueDate: z.string().min(1, "Due date is required"),
    tags: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.dueDate) return true;

      const [sd, sm, sy] = data.startDate.split("/").map(Number);
      const [dd, dm, dy] = data.dueDate.split("/").map(Number);

      const start = new Date(sy, sm - 1, sd);
      const due = new Date(dy, dm - 1, dd);

      return due >= start;
    },
    {
      message: "Due date must be after start date",
      path: ["dueDate"],
    }
  );

export const subscribeSchema = z.object({
  type: z.string().optional(),
  companyName: z
    .string({ error: "Company Name is required" })
    .min(1, "Company Name is required!"),
  shortName: z
    .string({ error: "Short name is required" })
    .min(1, "Short name is required!"),
  companyEmail: z
    .string({ error: "Company Email is required" })
    .min(1, "Company email is required!")
    .email("Invalid email format"),
  email: z
    .string({ error: "Email is required" })
    .min(1, "email is required!")
    .email("Invalid email format"),
  companyPhoneNumber: z
    .string({ error: "Company Phone number is required!" })
    .min(1, "Company Phone number is required!"),
  region: z
    .string({ error: "Region is required!" })
    .min(1, "Region is required!"),
  city: z.string({ error: "City is required!" }).min(1, "City is required!"),
  kifleketema: z
    .string({ error: "kifleketema is required!" })
    .min(1, "kifleketema is required!"),
  woreda: z
    .string({ error: "Woreda is required!" })
    .min(1, "Woreda is required!"),
  kebele: z
    .string({ error: "Kebele is required!" })
    .min(1, "Kebele is required!"),
  houseNumber: z.string().min(1, "houseNumber is required!"),
  phoneNumber: z.string().min(1, "phoneNumber is required!"),
  fullName: z
    .string({ error: "FullName is required!" })
    .min(3, "FullName is required!"),
  username: z
    .string({ error: "username is required!" })
    .min(1, "username is required!"),
});

export type ProductFormValues = z.infer<typeof orderSchema>;

export const userSchema = z.object(
  {
    fullName: z.string().min(1, "FullName is required!"),
   userName: z
    .string({ error: "username is required!" })
    .min(1, "username is required!"),
    email: z.email("Invalid contact email"),
    phoneNumber: z
      .string()
      .regex(/^\+?(\d{1,4})?\d{10}$/, "Invalid contact phone number"),
    role: z.string({ error: "role is required"}),
    bankAccount: z.string().optional(),
    serviceProviderId: z.string().optional(),
    selectedUnionId: z.string().optional(),
    selectedCooperativeId:z.string().optional()
})

export type UserFormValues = z.infer<typeof userSchema>;

export const inputSchema = z.object(
  {
    category: z.string().min(1, "Input category is required!"),
    name: z.string().min(1, "Name is required!"),
    unitOfMeasurementId: z.coerce
      .number()
      .min(1, "Unit of Measure is required!"),
    description: z.string().optional(),
    serviceProviderId: z.string().optional(),
    selectedUnionId: z.string().optional(),
    selectedCooperativeId: z.string().optional(),

  })

  export type InputFormValues = z.infer<typeof inputSchema>;

  export const outputSchema = z.object({
    productType: z.string().min(1, "Product type is required!"),
    name: z.string().min(1, "Name  is required!"),
    unitOfMeasurementId: z.coerce
      .number()
      .min(1, "Unit measure is required!"),
    variety: z.string().min(1, "Variety is required!"),
    description: z.string().optional(),
    tagKey: z.string().optional(),
    tagValue: z.string().optional(),
  })

  export type OutputFormValues = z.infer<typeof outputSchema>;

  export const farmingStageSchema = z.object({
    name: z.string().min(1, "Name is required!"),
    description: z.string().optional(),
    tagKey: z.string().optional(),
    tagValue: z.string().optional(),
  })

  export type FarmingStageFormValues = z.infer<typeof farmingStageSchema>;

const ORG_PHONE_REGEX = /^\+?(\d{1,4})?\d{10}$/;

export const organizationOptionalEmailField = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine(
    (val) => !val?.trim() || z.string().email().safeParse(val.trim()).success,
    "Invalid email",
  );

export const organizationOptionalPhoneField = z
  .string()
  .optional()
  .or(z.literal(""))
  .refine(
    (val) => !val?.trim() || ORG_PHONE_REGEX.test(val.trim()),
    "Invalid phone number",
  );

const organizationBankAccountDetailSchema = z.object({
  accountName: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine(
      (val) => !val?.trim() || /^[A-Za-z\s]+$/.test(val.trim()),
      "Account name must contain letters only",
    ),
  accountNumber: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine(
      (val) => !val?.trim() || /^\d+$/.test(val.trim()),
      "Account number must contain digits only",
    ),
  accountBranch: z.string().optional(),
});

export const organizationEditSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    shortName: z.string().optional(),
    type: z.string().optional(),
    email: organizationOptionalEmailField,
    phoneNumber: organizationOptionalPhoneField,
    address: z.object({
      region: z.string().optional(),
      city: z.string().optional(),
      kifleKetema: z.string().optional(),
      woreda: z.string().optional(),
      kebele: z.string().optional(),
      houseNumber: z.string().optional(),
    }),
    contactPerson: z.object({
      fullName: z.string().optional(),
      email: organizationOptionalEmailField,
      phoneNumber: organizationOptionalPhoneField,
      username: z.string().optional(),
    }),
    bankIds: z.array(z.number()).min(1, "Select at least one bank"),
    primaryBankId: z.number().optional(),
    bankAccountDetails: z.record(z.string(), organizationBankAccountDetailSchema),
  })
  .refine(
    (data) =>
      !data.primaryBankId || data.bankIds.includes(data.primaryBankId),
    {
      message: "Primary bank must be one of the selected banks",
      path: ["primaryBankId"],
    },
  );

export type OrganizationEditForm = z.infer<typeof organizationEditSchema>;


